import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraBadge, SpecraInput, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraCombobox, SpecraEntityCell, SpecraEntityMatchConfirm, SpecraFormField, SpecraInlineBanner, SpecraPersonOptionContent, SpecraSegmentedControl } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { useTenantAreas } from '../../lib/access/useTenantAreas';
import { KIND_TO_PROTO, LINKED_STATUSES, ResolutionStatus, ResolveMode, useDecideRegistryMatchCandidate, useResolveEntities } from './registryResolveClient';
import { resolverCandidates } from './toMatchCandidates';
import { useRegistryEntitySearch } from './useRegistryEntitySearch';
const T = Testids.registryPicker;
export const REGISTRY_KIND_NOUN = {
    CONTACT: 'person',
    COMPANY: 'company',
    GOVERNMENT_ORG: 'government office',
    VEHICLE: 'contract vehicle'
};
const KIND_TAB_LABEL = {
    CONTACT: 'People',
    COMPANY: 'Companies',
    GOVERNMENT_ORG: 'Offices',
    VEHICLE: 'Vehicles'
};
const NO_CREATE_MESSAGE = "You can't add records. Pick an existing one or ask a registry editor.";
/** Default create permission (GraphIdentityPicker's rule, kept). */ export function useRegistryCreatePermission(kind, override) {
    const areas = useTenantAreas();
    if (override !== undefined) return override;
    return areas.registries || kind === 'CONTACT' && (areas.isAdmin || areas.isInternal);
}
export function RegistryEntityPicker({ kinds, value, onPick, label, context, createMode = 'resolve', onCreateObservation, canCreate, initialObservation, selectedLabel, placeholder = 'Search people, companies, offices, vehicles', disabled, 'data-testid': testId = T.trigger }) {
    const [kind, setKind] = useState(kinds[0] ?? 'CONTACT');
    const [query, setQuery] = useState(initialObservation?.name ?? '');
    const [selected, setSelected] = useState();
    const [adding, setAdding] = useState(initialObservation?.name ?? (initialObservation ? '' : null));
    const search = useRegistryEntitySearch(kind, query);
    const mayCreate = useRegistryCreatePermission(kind, canCreate);
    const offerCreate = createMode !== 'none';
    const options = useMemo(()=>search.options.map((o)=>({
                value: o.registryId,
                label: o.label,
                textValue: o.label,
                content: o.kind === 'CONTACT' ? /*#__PURE__*/ _jsx(SpecraPersonOptionContent, {
                    name: o.label,
                    description: o.sublabel
                }) : /*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: o.label,
                    description: o.sublabel,
                    trailing: o.system ? /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "outline",
                        children: "Shared"
                    }) : undefined
                })
            })), [
        search.options
    ]);
    const pick = (option)=>{
        setSelected(option);
        onPick(option);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "min-w-0 space-y-2",
        "data-testid": T.combobox,
        children: [
            kinds.length > 1 && /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                label: "Record type",
                size: "sm",
                testId: T.kindSwitch,
                value: kind,
                options: kinds.map((k)=>({
                        value: k,
                        label: KIND_TAB_LABEL[k]
                    })),
                onValueChange: (k)=>{
                    setKind(k);
                    setQuery('');
                }
            }),
            /*#__PURE__*/ _jsx(SpecraCombobox, {
                label: label,
                placeholder: placeholder,
                searchPlaceholder: `Search ${KIND_TAB_LABEL[kind].toLowerCase()}…`,
                options: options,
                value: value || undefined,
                fallbackLabel: value ? selectedLabel : undefined,
                selectedOption: selected && selected.registryId === value ? {
                    value: selected.registryId,
                    label: selected.label
                } : undefined,
                disabled: disabled,
                shouldFilter: false,
                loading: search.loading && query.trim().length >= 2,
                searchValue: query,
                onSearchValueChange: setQuery,
                emptyMessage: search.isError ? 'Registry search is unavailable. Try again.' : query.trim().length < 2 ? 'Type at least 2 characters.' : offerCreate && !mayCreate ? NO_CREATE_MESSAGE : `No ${REGISTRY_KIND_NOUN[kind]} matches.`,
                onOptionChange: (opt)=>{
                    const found = search.options.find((o)=>o.registryId === opt?.value);
                    if (found) pick(found);
                },
                onCreate: offerCreate && mayCreate ? (q)=>setAdding(q) : undefined,
                canCreate: ()=>offerCreate && mayCreate,
                createLabel: (q)=>`Add ${REGISTRY_KIND_NOUN[kind]} “${q}”`,
                createTestId: T.create,
                getOptionTestId: ()=>T.option,
                triggerProps: {
                    'data-testid': testId
                }
            }),
            adding !== null && offerCreate && mayCreate && /*#__PURE__*/ _jsx(RegistryEntityAddDialog, {
                kind: kind,
                initialText: adding,
                initialObservation: initialObservation,
                context: context,
                createMode: createMode === 'observation' ? 'observation' : 'resolve',
                onCreateObservation: onCreateObservation,
                onPick: pick,
                onClose: ()=>setAdding(null)
            })
        ]
    });
}
function splitName(text) {
    const name = text.trim().replace(/\s+/g, ' ');
    const i = name.lastIndexOf(' ');
    return i > 0 ? {
        first: name.slice(0, i),
        last: name.slice(i + 1)
    } : {
        first: '',
        last: name
    };
}
/** The strong id of a draft (none for government offices: never auto-created). */ export function strongIdOf(draft) {
    switch(draft.kind){
        case 'CONTACT':
            return draft.email?.trim() ?? '';
        case 'COMPANY':
            return draft.uei?.trim() || draft.cage?.trim() || '';
        case 'VEHICLE':
            return draft.contractNumber?.trim() ?? '';
        default:
            return '';
    }
}
export function resolveModeFor(draft) {
    return draft.kind !== 'GOVERNMENT_ORG' && strongIdOf(draft) ? ResolveMode.MATCH_OR_CREATE : ResolveMode.MATCH_OR_CANDIDATE;
}
function draftLabel(d) {
    return d.kind === 'CONTACT' ? `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim() || (d.email ?? '') : (d.name ?? '').trim();
}
/**
 * "Add {noun}": strong identifier first, then REG-54 ResolveEntities. A match
 * or create closes with onPick; a name-only observation shows REG-54's
 * SpecraEntityMatchConfirm and decides the candidate in place.
 */ export function RegistryEntityAddDialog({ kind, initialText, initialObservation, context, createMode = 'resolve', onCreateObservation, onPick, onClose }) {
    const noun = REGISTRY_KIND_NOUN[kind];
    const initial = splitName(initialText);
    const [draft, setDraft] = useState(()=>kind === 'CONTACT' ? {
            kind,
            firstName: initial.first,
            lastName: initial.last
        } : {
            ...initialObservation,
            kind,
            name: initialText.trim()
        });
    const [pending, setPending] = useState(null);
    const [error, setError] = useState('');
    const resolve = useResolveEntities();
    const decide = useDecideRegistryMatchCandidate();
    const set = (patch)=>{
        setError('');
        setDraft((d)=>({
                ...d,
                ...patch
            }));
    };
    const label = draftLabel(draft);
    const valid = kind === 'VEHICLE' ? !!draft.name?.trim() && !!draft.contractNumber?.trim() : kind === 'CONTACT' ? !!label : !!draft.name?.trim() || !!strongIdOf(draft);
    const finish = (registryId, name)=>{
        onPick({
            kind,
            registryId,
            label: name || label
        });
        onClose();
    };
    const submit = async ()=>{
        if (!valid) return;
        if (createMode === 'observation') {
            onCreateObservation?.(draft);
            onClose();
            return;
        }
        try {
            const r = await resolve.mutateAsync({
                mode: resolveModeFor(draft),
                context,
                observation: {
                    kind: KIND_TO_PROTO[kind],
                    source: 'user',
                    name: kind === 'CONTACT' ? label : (draft.name ?? '').trim(),
                    firstName: draft.firstName?.trim() ?? '',
                    lastName: draft.lastName?.trim() ?? '',
                    email: draft.email?.trim() ?? '',
                    phone: draft.phone?.trim() ?? '',
                    title: draft.title?.trim() ?? '',
                    uei: draft.uei?.trim().toUpperCase() ?? '',
                    cage: draft.cage?.trim().toUpperCase() ?? '',
                    orgCode: draft.orgCode?.trim() ?? '',
                    agency: draft.agency?.trim() ?? '',
                    contractNumber: draft.contractNumber?.trim() ?? ''
                }
            });
            if (LINKED_STATUSES.has(r.status) && r.registryId) {
                finish(r.registryId, r.displayName);
            } else if (r.candidateId) {
                setPending({
                    candidateId: r.candidateId,
                    candidates: resolverCandidates(r.candidates),
                    observed: r.displayName || label
                });
            } else {
                setError(r.status === ResolutionStatus.REJECTED && r.errorMessage ? r.errorMessage : `This ${noun} could not be matched or held for confirmation.`);
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : `Couldn't add this ${noun}. Try again.`);
        }
    };
    const decideCandidate = async (decision, chosen)=>{
        if (!pending) return;
        try {
            const r = await decide.mutateAsync({
                candidateId: pending.candidateId,
                decision,
                chosenRegistryId: chosen
            });
            const chosenName = pending.candidates.find((c)=>c.id === chosen)?.name ?? pending.observed;
            if (r?.registryId) finish(r.registryId, decision === 'CONFIRM' ? chosenName : label);
            else setError('The registry returned no record. Try again.');
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not record that choice. Try again.');
        }
    };
    const busy = resolve.isPending || decide.isPending;
    const field = (id, fieldLabel, key, opts)=>/*#__PURE__*/ _jsx(SpecraFormField, {
            label: fieldLabel,
            htmlFor: `registry-add-${id}`,
            hint: opts?.hint,
            required: opts?.required,
            children: /*#__PURE__*/ _jsx(SpecraInput, {
                id: `registry-add-${id}`,
                type: opts?.type,
                value: draft[key] ?? '',
                disabled: busy || !!pending,
                "data-testid": opts?.strong ? T.strongId : undefined,
                onChange: (e)=>set({
                        [key]: e.target.value
                    })
            })
        });
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        onOpenChange: (open)=>{
            if (!open) onClose();
        },
        title: `Add ${noun}`,
        testId: T.addDialog,
        footerProps: pending ? {
            cancelLabel: 'Close',
            onCancel: onClose
        } : {
            cancelLabel: 'Cancel',
            onCancel: onClose,
            confirmLabel: `Add ${noun}`,
            onConfirm: submit,
            confirmDisabled: !valid,
            loading: busy,
            loadingLabel: 'Checking the registry…',
            confirmProps: {
                'data-testid': T.submit
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                kind === 'CONTACT' && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        field('email', 'Email', 'email', {
                            strong: true,
                            type: 'email',
                            hint: 'With an email we can match it to an existing person.'
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                            children: [
                                field('first', 'First name', 'firstName'),
                                field('last', 'Last name', 'lastName', {
                                    required: true
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                            children: [
                                field('title', 'Title', 'title'),
                                field('phone', 'Phone', 'phone')
                            ]
                        })
                    ]
                }),
                kind === 'COMPANY' && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        field('name', 'Name', 'name', {
                            required: true
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                            children: [
                                field('uei', 'UEI', 'uei', {
                                    strong: true,
                                    hint: 'With a UEI or CAGE we can match it to an existing record.'
                                }),
                                field('cage', 'CAGE', 'cage')
                            ]
                        })
                    ]
                }),
                kind === 'GOVERNMENT_ORG' && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        field('name', 'Name', 'name', {
                            required: true
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                            children: [
                                field('agency', 'Agency', 'agency'),
                                field('code', 'Office code', 'orgCode', {
                                    strong: true
                                })
                            ]
                        })
                    ]
                }),
                kind === 'VEHICLE' && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        field('name', 'Name', 'name', {
                            required: true
                        }),
                        field('contract', 'Contract number', 'contractNumber', {
                            strong: true,
                            required: true
                        })
                    ]
                }),
                pending && /*#__PURE__*/ _jsx(SpecraEntityMatchConfirm, {
                    kind: kind,
                    observedLabel: pending.observed,
                    candidates: pending.candidates,
                    state: busy ? 'pending' : 'ask',
                    onConfirm: (id)=>decideCandidate('CONFIRM', id),
                    onDecline: ()=>setPending({
                            ...pending,
                            candidates: []
                        }),
                    onCreateNew: kind === 'GOVERNMENT_ORG' ? undefined : ()=>decideCandidate('CREATE_NEW'),
                    onDefer: onClose
                }),
                error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    variant: "framed",
                    "data-testid": T.error,
                    children: error
                }),
                !pending && /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    className: "block",
                    size: "meta",
                    tone: "muted",
                    children: "Adds a shared record to Registries, visible beyond this page. A name alone is checked against existing records first."
                })
            ]
        })
    });
}
